// tax.js — Tax Module v1.2 (VIEWER architecture; simplification pass 2026-07-30)
// Layout mirrors the two "clean" pages 1:1 using their real shells:
//   Trades: .neo-hero + chips (no prose), grid4 .panel.kpi row, Proximity .track2
//           pattern (receipt window rail), .jrn-more folds.
//   Advisory: .mny-row queue — ACTIVE warnings only; the full W1-W12 catalog and
//           document inventory live inside folds.
// Data source unchanged: engine results.json snapshots via Supabase auto-load
// (tax_snapshots) or manual import; IDB-local only, never S / the cloud blob.

import { esc } from '../core/utils.js';
import { idbSaveTax, idbLoadTax } from '../core/persistence.js';
import { SB_REST, _sbHeaders, _sbUser } from '../core/auth.js';
import { toast } from '../ui/layout.js';
import { lg } from './history.js';
import { S } from '../core/state.js';
import { taxRefundBooking, taxRefundTagged } from '../core/integrity.js';
import { publishTaxRefunds, publishTaxSnapshots } from '../core/tax-bridge.js';
import { markDirty, renderOnly } from '../app/render-loop.js';
import { openOTIFor } from './cashflow.js';

let _years = {};
let _importedAt = {};
let _sel = null;
const _openW = new Set(['W5']);

// ── formatting ──
const _fT = (n, dec = 0) => '₺' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec });
const _fTk = n => _fT(n, 2);
const _fC = n => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e6) return '₺' + (v / 1e6).toFixed(2) + 'M';
  if (Math.abs(v) >= 1e3) return '₺' + (v / 1e3).toFixed(0) + 'k';
  return _fT(v);
};
const _pct = p => Math.round((p || 0) * 100) + '%';
const MON3 = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// ── persistence + cloud auto-load (unchanged behavior) ──
// Has anyone read the IDB store yet? Distinct from "are there years in it" — see _publish().
let _loaded = false;

export async function initTax() {
  const stored = await idbLoadTax();
  _loaded = true;
  if (stored && stored.years) {
    _years = stored.years;
    _importedAt = stored.imported_at || {};
    _sent = stored.sent_windows || {};
    _pruneSent();
  }
  const ys = Object.keys(_years).map(Number).sort((a, b) => b - a);
  if (ys.length && !_sel) _sel = ys[0];
  renderTax();
  _cloudFetch(0);
}

async function _persist() {
  await idbSaveTax({ years: _years, imported_at: _importedAt, sent_windows: _sent });
}

// owner's "sent to payroll" marks, keyed 'YYYY-MM' (device-local process tracking;
// the engine's payslip verdict is the only truth and prunes these on PASS)
let _sent = {};

function _pruneSent() {
  Object.keys(_sent).forEach(k => {
    const y = parseInt(k.slice(0, 4), 10), m = parseInt(k.slice(5, 7), 10);
    const r = _years[y];
    const cm = r && (r.cutoff_months || []).find(x => x.month === m);
    if (cm && cm.status === 'pass') delete _sent[k];
  });
}

export function taxMarkSent(y, m) {
  const k = y + '-' + String(m).padStart(2, '0');
  if (_sent[k]) delete _sent[k];
  else _sent[k] = new Date().toISOString().slice(0, 10);
  _persist();
  renderTax();
}

async function _cloudFetch(attempt) {
  const MAX_TRIES = 8;
  try {
    if (!_sbUser || !_sbUser.id) throw new Error('auth-not-ready');
    const headers = await _sbHeaders();
    if (!headers) throw new Error('no-token');
    const resp = await fetch(SB_REST + '/tax_snapshots?user_id=eq.' + _sbUser.id + '&select=tax_year,data,updated_at', { headers });
    if (!resp.ok) {
      if (resp.status === 404) return;
      throw new Error('http-' + resp.status);
    }
    const rows = await resp.json();
    let changed = false;
    (rows || []).forEach(row => {
      const res = row.data;
      if (!res || res.schema_version !== 1 || !Number.isInteger(res.tax_year)) return;
      const local = _years[res.tax_year];
      if (!local || String(res.generated || '') >= String(local.generated || '')) {
        _years[res.tax_year] = res;
        _importedAt[res.tax_year] = row.updated_at || new Date().toISOString();
        changed = true;
      }
    });
    if (changed) {
      if (!_sel) _sel = Object.keys(_years).map(Number).sort((a, b) => b - a)[0];
      _pruneSent();
      _persist();
      renderTax();
    }
  } catch (_e) {
    if (attempt < MAX_TRIES) setTimeout(() => _cloudFetch(attempt + 1), 2500);
  }
}

// ── import fallback ──
export function taxPickFile() {
  const el = document.getElementById('taxFile');
  if (el) { el.value = ''; el.click(); }
}

export function taxImportFile(e) {
  const file = e.target && e.target.files && e.target.files[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = () => {
    try {
      const res = JSON.parse(reader.result);
      if (res.schema_version !== 1 || !Number.isInteger(res.tax_year)) {
        toast('Not a tax engine snapshot (expected results.json, schema v1)', 'a-r');
        return;
      }
      _years[res.tax_year] = res;
      _importedAt[res.tax_year] = new Date().toISOString();
      _sel = res.tax_year;
      _persist();
      lg('tax', String(res.tax_year), 'results.json imported (engine run ' + (res.generated || '?') + ')');
      toast('Tax snapshot ' + res.tax_year + ' imported', 'a-b');
      renderTax();
    } catch (_err) {
      toast('Could not read snapshot: invalid JSON', 'a-r');
    }
  };
  reader.readAsText(file);
}

export function taxSelectYear(y) {
  _sel = Number(y);
  renderTax();
}

export function taxToggleWarn(id) {
  if (_openW.has(id)) _openW.delete(id); else _openW.add(id);
  renderTax();
}

// ── building blocks (real app shells) ──
function _hero(label, num, numCls, chips, ring) {
  return '<div class="neo-hero" style="--hx:#FF4D6A;border-color:rgba(255,77,106,.35)">' +
    '<div class="neo-hleft">' +
      '<div class="neo-hlbl">' + label + '</div>' +
      '<div class="neo-hnum ' + numCls + '">' + num + '</div>' +
      '<div class="neo-chips">' + chips + '</div>' +
    '</div>' + ring + '</div>';
}

function _ring(gradId, stops, offset, big, small) {
  return '<div class="neo-ring"><svg width="124" height="124" viewBox="0 0 124 124">' +
    '<defs><linearGradient id="' + gradId + '" x1="0" y1="0" x2="1" y2="1">' + stops + '</linearGradient></defs>' +
    '<circle class="bgc" cx="62" cy="62" r="51.4"/>' +
    '<circle class="fgc" cx="62" cy="62" r="51.4" style="stroke:url(#' + gradId + ');stroke-dashoffset:' + offset + ';stroke-dasharray:323"/></svg>' +
    '<div class="rlbl"><b style="color:#ff7a95">' + big + '</b><span>' + small + '</span></div></div>';
}

const _chip = (l, v, cls) => '<span class="neo-chip">' + l + ' <b' + (cls ? ' class="' + cls + '"' : '') + '>' + v + '</b></span>';

function _kpi(lbl, v, cls, sub) {
  return '<div class="panel kpi"><div class="lbl">' + lbl + '</div><div class="v" style="color:' + cls + '">' + v + '</div><div class="ksub">' + sub + '</div></div>';
}

function _wRow(id, rt, rsum, sev, detail) {
  const open = _openW.has(id);
  const ac = sev === 'warn' ? 'var(--yel)' : sev === 'block' ? 'var(--red)' : sev === 'ok' ? 'var(--grn)' : 'var(--cyan)';
  return '<div class="mny-row' + (open ? ' open' : '') + '" style="--ac:' + ac + '">' +
    '<div class="mny-rmain" data-action="taxToggleWarn" data-args=\'["' + id + '"]\'>' +
    '<span class="dot"></span>' +
    '<span class="tax-wid">' + id + '</span>' +
    '<span class="rt" style="font-weight:500;color:var(--t2)">' + rt + '</span>' +
    '<span class="rsum">' + rsum + '</span>' +
    '<span class="chev">▾</span></div>' +
    '<div class="mny-rdetail"><div class="mny-rinner">' + detail + '</div></div></div>';
}

// ── live year view (v0.3 structure) ──
function _nextDeadline(cutoff) {
  const now = new Date();
  let y = now.getFullYear(), m = now.getMonth();
  if (now.getDate() > cutoff) { m += 1; if (m > 11) { m = 0; y += 1; } }
  const deadline = new Date(y, m, cutoff);
  return { y, m, days: Math.max(0, Math.ceil((deadline - now) / 864e5)) };
}

const _CHK = '<svg width="14" height="14" viewBox="0 0 16 16"><path d="M3 8l3 3 7-7" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/></svg>';
const _X = '<svg width="12" height="12" viewBox="0 0 12 12"><path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
const _DOTS = '<svg width="12" height="12" viewBox="0 0 12 12"><circle cx="6" cy="6" r="2" fill="currentColor" opacity="0.3"/></svg>';

function _receiptWindow(r) {
  const cutoff = r.payroll_cutoff_day || 10;
  const now = new Date();
  const nd = _nextDeadline(cutoff);
  const m = nd.m, daysLeft = nd.days;
  // Proximity-style rail: the rail IS the submission window (opens on the 1st,
  // deadline on the cutoff day = TP1 position). Cursor = today marching right.
  const sentKey = nd.y + '-' + String(m + 1).padStart(2, '0');
  const winStart = new Date(nd.y, m, 1);
  const span = cutoff - 1; // days from the 1st to the deadline
  const dayIn = (now - winStart) / 864e5;
  const curPos = Math.max(0, Math.min(100, (dayIn / span) * 100));
  // month milestones (Goals "Net Worth Milestones" recipe: gls-mile circles)
  const mine = r.cutoff_months || [];
  const miles = mine.map(x => {
    const isNext = x.status === 'pending' && x.month === (r.months_parsed || 0) + 1;
    const cls = x.status === 'pass' ? 'done' : x.status === 'miss' ? 'miss' : isNext ? 'cur' : 'lock';
    const inner = x.status === 'pass' ? _CHK : x.status === 'miss' ? _X : isNext ? daysLeft + 'd' : _DOTS;
    return '<div class="gls-mile ' + cls + '"><div class="c">' + inner + '</div><span>' + MON3[x.month - 1] + '</span></div>';
  }).join('');
  const missN = mine.filter(x => x.status === 'miss').length;
  return '<div class="panel" style="margin-bottom:18px">' +
    '<h2 class="pt" style="margin-bottom:6px">Receipt window <span class="tax-hint">· submit to payroll by the ' + cutoff + 'th · 615 verified at payslip</span></h2>' +
    '<div class="tax-pr">' +
      '<div class="tax-pid"><div class="t"><span class="tk" style="width:24px;height:24px;font-size:9px">' + MON3[m].toUpperCase() + '</span>' + MON3[m] + '</div>' +
        '<div class="s">window open</div></div>' +
      '<div class="track2"><div class="rail"></div>' +
        '<div class="tick en" style="left:0"></div><div class="lab" style="left:0;transform:none">opens ' + MON3[m] + ' 1</div>' +
        '<div class="tick tp" style="left:100%"></div><div class="lab" style="left:100%;transform:translateX(-100%)">deadline ' + MON3[m] + ' ' + cutoff + '</div>' +
        '<div class="cur" style="left:' + curPos.toFixed(1) + '%"></div>' +
      '</div>' +
      '<div class="tax-stat"><div class="big' + (daysLeft <= 3 ? '' : ' pos') + '"' + (daysLeft <= 3 ? ' style="color:var(--yel)"' : '') + '>' + daysLeft + ' days</div>' +
        '<div class="sm">to deadline</div><div class="sm">miss → Mar ' + (r.tax_year + 1) + ' · −25-30% real</div>' +
        (_sent[sentKey]
          ? '<div class="tax-sentok" data-action="taxMarkSent" data-args=\'[' + nd.y + ',' + (m + 1) + ']\' title="Click to undo">✓ sent ' + _sent[sentKey] + ' · awaiting payslip</div>'
          : '<button class="tax-sentbtn" data-action="taxMarkSent" data-args=\'[' + nd.y + ',' + (m + 1) + ']\'>Sent to payroll ✓</button>') +
        '</div>' +
    '</div>' +
    '<div class="gls-miles" style="margin-top:14px;padding-top:14px;border-top:1px solid rgba(255,255,255,.04)">' + miles + '</div>' +
    (missN ? '<div class="tax-hint" style="text-align:center;display:block;margin-top:8px;font-family:var(--mono)">' +
      mine.filter(x => x.status === 'pass').length + ' verified to the kurus · <span style="color:var(--red)">' + missN + ' missed → declaration</span></div>' : '') +
    '</div>';
}

// Advisory-hero-style card (the NEXT BEST MOVES look): icon badge, big title,
// prose body, glowing action pill, ENGINE meta corner.
function _wCard(ac, icon, title, rank, body, action, why) {
  return '<div class="tax-wcard" style="--hac:' + ac + '">' +
    '<div class="tax-wtop"><div class="tax-wic">' + icon + '</div>' +
      '<div class="tax-wtitle">' + title + '</div><span class="tax-wrank">' + rank + '</span></div>' +
    '<div class="tax-wsub">' + body + '</div>' +
    '<div class="tax-wbtns">' +
      (action ? '<span class="tax-wgo">' + action + ' →</span>' : '') +
      '<span class="tax-wwhy">' + why + '</span>' +
    '</div></div>';
}

function _w5Breakdown(texts) {
  // "W5 CUTOFF MISS 2026-02: window 36,465.00 vs 615 19,168.84 -> 17,296.16 to declaration ledger"
  const parts = [];
  texts.forEach(t => {
    const m = String(t).match(/(\d{4})-(\d{2}).*?->\s*([\d,.]+)\s+to declaration/);
    if (m) parts.push(MON3[parseInt(m[2], 10) - 1] + ' <b>₺' + m[3] + '</b>');
  });
  return parts.join(' · ');
}

function _activeWarnings(r) {
  const marg = r.marginal_rate || 0;
  const byId = {};
  (r.warnings || []).forEach(w => { (byId[w.id] = byId[w.id] || []).push(w.text); });
  const cards = [];
  const missed = r.missed_window_total || 0;
  const nd = _nextDeadline(r.payroll_cutoff_day || 10);
  if (missed > 0.01) {
    const bd = _w5Breakdown(byId.W5 || []);
    // owner marked a month "sent" but the payslip verdict came back MISS -> escalate
    const disputed = (r.cutoff_months || [])
      .filter(x => x.status === 'miss' && _sent[r.tax_year + '-' + String(x.month).padStart(2, '0')])
      .map(x => MON3[x.month - 1] + ' (marked sent ' + _sent[r.tax_year + '-' + String(x.month).padStart(2, '0')] + ')')
      .join(', ');
    cards.push(_wCard('var(--yel)', '₺',
      _fTk(missed) + ' missed payroll windows — ledgered to Mar ' + (r.tax_year + 1), 'W5',
      (bd ? bd + ' — these ' : 'These ') + 'receipt windows never reached payroll: the receipts arrived after the ' +
      (r.payroll_cutoff_day || 10) + 'th and SAP applies no retro processing. The amounts are frozen into the Mar ' +
      (r.tax_year + 1) + ' declaration, worth ≈<b>' + _fT(missed * marg) + '</b> at ' + _pct(marg) +
      ' marginal — recoverable, but the year of waiting is the real cost.' +
      (disputed ? '<br><b style="color:var(--red)">You marked ' + disputed + ' as sent — the payslip disagrees. Escalate with payroll.</b>' : ''),
      'Get ' + MON3[nd.m] + ' receipts to payroll by the ' + (r.payroll_cutoff_day || 10) + 'th — ' + nd.days + ' days left',
      'ENGINE · W5 · HIGH'));
  }
  const w9 = byId.W9 || [];
  if (w9.length) {
    cards.push(_wCard('var(--yel)', '◈',
      w9.length + ' attested item' + (w9.length > 1 ? 's' : '') + ' await documents', 'W9',
      w9.map(esc).join('<br>') + '<br>Amounts are tracked from your attestation, but the declaration needs each document\'s serial, date and provider tax id — filing blocks without them.',
      'Chase the missing PDFs before declaration season', 'ENGINE · W9 · MEDIUM'));
  }
  const w2 = byId.W2 || [];
  if (w2.length) {
    cards.push(_wCard('var(--yel)', '≠', 'Education invoice could not be split — review parse', 'W2',
      w2.map(esc).join('<br>'), 'Check the flagged invoice layout', 'ENGINE · W2 · MEDIUM'));
  }
  const proof = r.tariff_proof || {};
  if (proof.pass === false) {
    cards.push(_wCard('var(--red)', '✕', 'Tariff proof FAILED — stop trusting these figures', 'W11',
      'Engine tariff on the cumulative base gives <b>' + _fTk(proof.engine) + '</b> but payslips show <b>' + _fTk(proof.payslips) +
      '</b>. Validation protocol rule 1: a data or parameter problem exists — resolve it first.',
      'Re-check params and the latest payslip', 'ENGINE · W11 · CRITICAL'));
  } else if (String(r.params_status || '').indexOf('DRAFT') >= 0) {
    cards.push(_wCard('var(--cyan)', '◔', r.tax_year + ' parameters are DRAFT until the December tariff', 'W11',
      'Every figure on this page uses the draft ' + r.tax_year + ' parameter set — already payslip-proven to 5 kurus, so the risk is low. ' +
      'The final tariff lands Dec 30-31 (revaluation rate ~Nov 25-28); scheduled watchers pick both up automatically.',
      '', 'ENGINE · W11 · INFO'));
  }
  Object.keys(byId).filter(k => ['W5', 'W9', 'W2'].indexOf(k) < 0).forEach(k => {
    cards.push(_wCard('var(--yel)', '◆', 'Engine warning', esc(k), byId[k].map(esc).join('<br>'), '', 'ENGINE · ' + esc(k)));
  });
  if (!cards.length) {
    cards.push(_wCard('var(--grn)', '✓', 'All clear — no active warnings', '',
      'Every payroll window reconciled to the kurus, every expected document accounted for.', '', 'ENGINE'));
  }
  return cards.join('');
}

// donut segment helper: pct 0-1, off = cumulative pct before this segment
function _seg(pct, off, color) {
  const C = 2 * Math.PI * 40; // r=40
  return '<circle cx="50" cy="50" r="40" fill="none" stroke="' + color + '" stroke-width="14" ' +
    'stroke-dasharray="' + (pct * C).toFixed(1) + ' ' + C.toFixed(1) + '" ' +
    'stroke-dashoffset="' + (-off * C).toFixed(1) + '" transform="rotate(-90 50 50)"/>';
}

// mini capacity ring (hero-ring recipe at 84px): pct 0-1
function _miniRing(id, pct, color1, color2, big, small) {
  const C = 2 * Math.PI * 44; // r=44 on a 110px ring
  const off = (C * (1 - Math.min(1, pct || 0))).toFixed(1);
  return '<div class="tax-mring"><svg width="110" height="110" viewBox="0 0 110 110">' +
    '<defs><linearGradient id="' + id + '" x1="0" y1="0" x2="1" y2="1">' +
    '<stop offset="0%" stop-color="' + color1 + '"/><stop offset="100%" stop-color="' + color2 + '"/></linearGradient></defs>' +
    '<circle cx="55" cy="55" r="44" fill="none" stroke="rgba(150,165,200,.12)" stroke-width="8" />' +
    '<circle cx="55" cy="55" r="44" fill="none" stroke="url(#' + id + ')" stroke-width="8" stroke-linecap="round" ' +
      'stroke-dasharray="' + C.toFixed(1) + '" stroke-dashoffset="' + off + '" transform="rotate(-90 55 55)"/></svg>' +
    '<div class="dl"><b>' + big + '</b><span>' + small + '</span></div></div>';
}

// "Deductions & documents" — split cards: donut mix · ceiling ring · envelope ring,
// then a Payslips streak card (Goals Investment Streak recipe) with the doc counters
function _deductionPanel(r) {
  const edu = (r.education && r.education.deductible) || 0;
  const missed = r.missed_window_total || 0;
  const health = r.health_declaration_total || 0;
  const total = r.declaration_deductions_total || (edu + missed + health) || 1;
  const pE = edu / total, pM = missed / total, pH = health / total;
  const ic = r.insurance_ceiling || {}, env = r.envelope_10pct || {};
  const mp = r.months_parsed || 0;
  const nd = _nextDeadline(r.payroll_cutoff_day || 10);
  const dots = Array.from({ length: 12 }, (_, i) => '<i class="' + (i < mp ? 'on' : '') + '"></i>').join('');
  return '<div class="tax-mixrow">' +
    '<div class="panel tax-mixcard">' +
      '<h2 class="pt" style="margin-bottom:8px">Deduction mix <span class="tax-hint">· ' + _fC(total) + ' declared</span></h2>' +
      '<div style="display:flex;gap:20px;align-items:center;flex-wrap:wrap">' +
        '<div class="tax-donutwrap"><svg width="110" height="110" viewBox="0 0 100 100">' +
          _seg(pE, 0, 'var(--grn)') + _seg(pM, pE, 'var(--ora)') + _seg(pH, pE + pM, 'var(--cyan)') +
          '</svg><div class="dl"><b>' + _fC(total) + '</b><span>DECLARED</span></div></div>' +
        '<div class="legend" style="flex:1;min-width:180px">' +
          '<div><i style="background:var(--grn)"></i>Education <b>' + _fT(edu) + '</b></div>' +
          '<div><i style="background:var(--ora)"></i>Missed windows <b>' + _fT(missed) + '</b></div>' +
          '<div><i style="background:var(--cyan)"></i>Health <b>' + _fT(health) + '</b></div>' +
        '</div>' +
      '</div></div>' +
    '<div class="panel tax-ringcard">' +
      '<h2 class="pt">Insurance ceiling</h2>' +
      _miniRing('taxRingIc', ic.pct, 'var(--yel)', 'var(--ora)', _pct(ic.pct), 'USED') +
      '<div class="rsub">' + _fC(ic.used) + ' / ' + _fC(ic.cap) + '</div></div>' +
    '<div class="panel tax-ringcard">' +
      '<h2 class="pt">Envelope 10%</h2>' +
      _miniRing('taxRingEnv', env.pct, 'var(--vio)', 'var(--cyan)', _pct(env.pct), 'USED') +
      '<div class="rsub">' + _fC(env.used) + ' / ' + _fC(env.cap) + '</div></div>' +
  '</div>' +
  '<div class="panel" style="margin-bottom:18px">' +
    '<div style="display:flex;gap:24px;align-items:center;flex-wrap:wrap">' +
      '<div style="flex:1;min-width:260px">' +
        '<h2 class="pt">Payslips <small style="font-size:11px;color:var(--t3);font-weight:400">next: ' + MON3[nd.m] + ' payslip</small></h2>' +
        '<div style="font-family:var(--mono);font-size:24px;font-weight:700;color:var(--grn)">' + mp + '/12</div>' +
        '<div class="gls-dots">' + dots + '</div>' +
      '</div>' +
      '<div class="tax-docrow" style="flex:none">' +
        '<span>Receipts <b>' + (r.receipts_count || 0) + '</b></span>' +
        '<span>Education docs <b>' + (r.education_docs || 0) + '</b></span>' +
        '<span>615 processed <b>' + _fT(r.payroll_615_total) + '</b></span>' +
      '</div>' +
    '</div></div>';
}

function _folds(r) {
  const missed = (r.missed_window_total || 0) > 0.01;
  const w9n = (r.warnings || []).filter(w => w.id === 'W9').length;
  const draft = String(r.params_status || '').indexOf('DRAFT') >= 0;
  const ic = r.insurance_ceiling || {}, env = r.envelope_10pct || {};
  const st = (cond, active, ok) => '<span class="bdg">' + (cond ? active : ok) + '</span>';
  const rules = [
    ['W1', 'double-SGK on wage line', '<span class="bdg">BLOCK at filing</span>'],
    ['W2', 'meals inside education total', '<span class="bdg">BLOCK at filing</span>'],
    ['W3', 'payroll/declaration double-dip', '<span class="bdg">BLOCK at filing</span>'],
    ['W4', 'installment outside tax year', '<span class="bdg">BLOCK at filing</span>'],
    ['W5', 'payroll cutoff miss', st(missed, 'ACTIVE', 'OK')],
    ['W6', 'exemption form equivalence', '<span class="bdg">INFO</span>'],
    ['W7', 'insurance ceiling · ' + _pct(ic.pct), st((ic.pct || 0) >= 0.9, 'WARN', 'OK')],
    ['W8', 'education+health envelope · ' + _pct(env.pct), st((env.pct || 0) >= 0.9, 'WARN', 'OK')],
    ['W9', 'doc completeness', st(w9n > 0, 'ACTIVE', 'OK')],
    ['W10', 'missing expected month', '<span class="bdg">OK</span>'],
    ['W11', 'parameter staleness', st(draft, 'DRAFT', 'LOCKED')],
    ['W12', 'capacity honesty', '<span class="bdg">IDLE</span>']
  ].map(x => '<div><b>' + x[0] + '</b>' + x[1] + ' ' + x[2] + '</div>').join('');
  return '<div class="panel" style="margin-bottom:14px">' +
    '<details class="jrn-more" style="border-top:none;padding-top:0;margin-top:0"><summary>All 12 rules — every one born from a real error</summary>' +
      '<div class="tax-rules">' + rules + '</div></details>' +
    '</div>';
}

// Declaration pack (Phase 2): engine maintains the filing pack on the mac —
// DRAFT while the gate is dirty, the real pack once it clears. This panel only
// mirrors the snapshot's declaration_pack block; the app computes nothing.
function _packPanel(r) {
  const p = r.declaration_pack;
  if (!p) return '';
  const draft = p.mode !== 'full';
  const f = p.fields || {}, d = p.docs || {};
  const docsTotal = (d.payslips || 0) + (d.viennalife_receipts || 0) + (d.axa_pdfs || 0) +
    (d.education || 0) + (d.health_paper_only || 0);
  const chip = draft
    ? '<span class="tax-pkchip warn">DRAFT · NOT FOR FILING</span>'
    : '<span class="tax-pkchip ok">READY · GATE PASS</span>';
  const body = draft
    ? (p.blockers || []).map(b => '<div class="tax-pkb">✕ ' + esc(String(b)) + '</div>').join('')
    : '<div class="tax-pkb ok">✓ Gate clear — filing sheet, evidence bundle and refund petition are built</div>';
  const stats = f.insurance == null ? '' :
    '<div class="tax-pkstats">' +
      '<div><span>GIB FIELD · INSURANCE</span><b>' + _fTk(f.insurance) + '</b></div>' +
      '<div><span>GIB FIELD · EDU+HEALTH</span><b>' + _fTk(f.education_health) + '</b></div>' +
      '<div><span>EVIDENCE DOCS</span><b>' + docsTotal + '</b></div>' +
    '</div>';
  return '<div class="mny-qhd">DECLARATION PACK · FILES MAR ' + (r.tax_year + 1) + '</div>' +
    '<div class="panel" style="margin-bottom:18px">' +
    '<div style="display:flex;gap:24px;align-items:flex-start;flex-wrap:wrap">' +
      '<div style="flex:1;min-width:250px">' +
        '<h2 class="pt" style="display:flex;gap:10px;align-items:center;flex-wrap:wrap">Filing pack ' + chip + '</h2>' +
        '<div class="tax-hint" style="margin:2px 0 8px">auto-rebuilt on every document drop · ' + esc(String(p.generated || '')) + '</div>' +
        body +
        '<div class="tax-hint" style="margin-top:8px">taxes/' + r.tax_year + ' tax year/declaration_pack/' +
          (draft ? 'FILING_SHEET_DRAFT.md' : 'FILING_SHEET.md') + '</div>' +
      '</div>' + stats +
    '</div></div>';
}

function _liveView(r) {
  const marg = r.marginal_rate || 0;
  const missed = r.missed_window_total || 0;
  const pj = r.year_end_projection;
  const target = pj && pj.refund_expected;
  // ring: with a target = how far along the money is; without = how far along the year is
  const off = target
    ? Math.round(323 * (1 - Math.min(1, (r.banked_refund || 0) / target)))
    : Math.round(323 * (1 - (r.months_parsed || 0) / 12));
  const ringBig = target ? _pct((r.banked_refund || 0) / target) : (r.months_parsed || 0) + '/12';
  const ringSmall = target ? 'OF TARGET' : 'MONTHS IN';
  const num = _fT(r.banked_refund) + (target ? ' <span class="tax-target">/ ' + _fC(target) + '</span>' : '');
  const chips =
    _chip('Months', (r.months_parsed || 0) + '/12') +
    (pj ? _chip('Band', _fC(pj.refund_low) + '–' + _fC(pj.refund_high)) : '') +
    _chip('Deductions', _fT(r.declaration_deductions_total)) +
    _chip('Marginal', _pct(marg)) +
    (missed > 0.01 ? _chip('Missed', _fT(missed), 'neg') : '') +
    _chip('Proof', r.tariff_proof && r.tariff_proof.pass ? 'PASS' : 'FAIL', r.tariff_proof && r.tariff_proof.pass ? 'pos' : 'neg');
  const ic = r.insurance_ceiling || {};
  return _hero('BANKED REFUND · YEAR-END TARGET · DECLARED MAR ' + (r.tax_year + 1), num, 'pos', chips,
      _ring('taxGradR', '<stop offset="0%" stop-color="#FF4D6A"/><stop offset="100%" stop-color="#ff8c42"/>', off,
        ringBig, ringSmall)) +
    '<div class="grid4">' +
      _kpi('Cum Tax Base', _fC(r.cum_tax_base), '#ff7a95', 'thru month ' + (r.months_parsed || 0)) +
      _kpi('Education Ded', _fT(r.education && r.education.deductible), 'var(--grn)', 'meals excluded') +
      _kpi('Insurance Ceiling', _pct(ic.pct), 'var(--yel)', _fC(ic.used) + ' / ' + _fC(ic.cap)) +
      _kpi('Missed → Mar ' + (r.tax_year + 1), _fT(missed), 'var(--ora)', '≈ +' + _fT(missed * marg) + ' refund') +
    '</div>' +
    _receiptWindow(r) +
    _deductionPanel(r) +
    _packPanel(r) +
    '<div class="mny-qhd">WARNINGS · ACTIVE ONLY, MONEY FIRST</div>' +
    _activeWarnings(r) +
    _folds(r);
}

// ── full / validated year view ──
function _fullYearView(r) {
  const rep = r.declaration_replay || {};
  const val = r.official_validation;
  const validated = val && rep.refund != null && Math.abs(rep.refund - val.refund_confirmed) < 0.01;
  const fh = r.filing_history;
  const chips =
    _chip('Tax base', _fC(rep.tax_base)) +
    _chip('Deductions', _fT(rep.deductions)) +
    (fh ? _chip('Filed naively', _fTk(fh.filed_refund), 'neg') : '') +
    _chip('Exemption', _fT(rep.exemption));
  let html = '';
  if (validated) {
    const paid = val.refund_paid != null;
    html += '<div class="tax-vband"><span class="badge">✓ YEAR VALIDATED</span>' +
      '<span>Confirmed by ' + esc(String(val.confirmed_by || '')) + ' (' + esc(String(val.date || '')) + ') · reproduced to the kurus · archive locked</span>' +
      (paid ? '<span style="margin-left:auto;font-family:var(--mono);font-size:11px;color:var(--grn);white-space:nowrap">' +
        '<b>REFUND PAID ' + _fTk(val.refund_paid) + '</b> · ' + esc(String(val.paid_date || '')) +
        ' · EFT · Δ ' + Number(val.paid_vs_assessed_delta || 0).toFixed(2) + '</span>' : '') +
      '</div>';
  }
  html += _hero((validated ? 'CONFIRMED' : 'ENGINE') + ' REFUND · TAX YEAR ' + r.tax_year, _fTk(rep.refund), 'pos', chips,
    _ring('taxGradV', '<stop offset="0%" stop-color="#34e2a0"/><stop offset="100%" stop-color="#00f0ff"/>', 0,
      validated ? '✓' : '12/12', validated ? 'VALIDATED' : 'FULL YEAR'));
  if (fh && fh.errors && fh.errors.length) {
    html += '<div class="mny-qhd">THE ' + _fC(fh.filed_refund - rep.refund) + ' ERROR CLASS · NOW PERMANENT RULES</div>';
    html += fh.errors.map(er => _wRow(esc(String(er.id || '')),
      '<b style="color:var(--t1)">' + _fTk(er.amount) + '</b> ' + esc(String(er.label || '')),
      '', 'block', esc(String(er.note || '')))).join('');
  }
  html += '<div class="panel" style="margin-bottom:14px">' +
    '<details class="jrn-more" style="border-top:none;padding-top:0;margin-top:0"><summary>Declaration replay — the math</summary>' +
      '<div class="tax-docrow" style="flex-direction:column;gap:6px">' +
        '<span>Tax base <b>' + _fTk(rep.tax_base) + '</b> − deductions <b>' + _fTk(rep.deductions) + '</b> → declared base <b>' + _fTk(rep.declared_base) + '</b></span>' +
        '<span>Calculated tax <b>' + _fTk(rep.calculated_tax) + '</b> − exemption <b>' + _fTk(rep.exemption) + '</b> → due <b>' + _fTk(rep.due) + '</b></span>' +
        '<span>Withheld <b>' + _fTk(rep.withheld_net) + '</b> − due → refund <b style="color:var(--grn)">' + _fTk(rep.refund) + '</b></span>' +
      '</div></details>' +
    '<details class="jrn-more"><summary>Documents &amp; sources</summary>' +
      '<div class="tax-docrow">' +
        '<span>Payslips <b>' + (r.months_parsed || 0) + '/12</b></span>' +
        '<span>Insurance receipts <b>' + (r.receipts_count || 0) + '</b></span>' +
        '<span>Education docs <b>' + (r.education_docs || 0) + '</b></span>' +
        '<span>Ceiling <b>' + _pct(r.insurance_ceiling && r.insurance_ceiling.pct) + '</b></span>' +
        '<span>Status <b>' + (validated ? 'LOCKED · golden case' : 'awaiting confirmation') + '</b></span>' +
      '</div></details></div>';
  html += _packPanel(r);
  return html;
}

// ── the one thing this module publishes to the money side ──
//
// Snapshots stay out of S on purpose — they are an engine artefact, they are large, and the
// cloud blob is not their home. But a refund that has been PAID is not an assessment, it is
// money that moved, and the rest of the app has a right to know it moved. So exactly three
// facts cross the boundary: which year, how much, and on what date. Nothing writes them
// into S; callers read them live.

/**
 * ONE PREDICATE, THREE CALLERS. The watchdog, the on-page band and the booking button each
 * used to decide for themselves whether a snapshot carried a paid refund, and they disagreed:
 * a `refund_paid` the engine emitted as a STRING was dropped by the publisher (so the check
 * reported "no refund paid yet", green) while the band on the same data shouted NOT IN CASH.
 * The check went quiet on exactly the money it exists to watch. Snapshots are external JSON
 * from a separate Python tool, so this coerces rather than type-tests. (audit 2026-08-10)
 *
 * @returns {Object|null} the paid-refund facts, or null if this year has no payment on record
 */
function _paidRefund(r) {
  const v = r && r.official_validation;
  if (!v) return null;
  const amt = Number(v.refund_paid);
  const date = v.paid_date ? String(v.paid_date).slice(0, 10) : '';
  if (!isFinite(amt) || amt <= 0 || !date) return null;
  return {
    year: r.tax_year,
    amount: amt,
    paidDate: date,
    currency: 'TRY',
    ref: String(v.payment_channel || ''),
    confirmedBy: String(v.confirmed_by || ''),
    evidence: String(v.evidence || ''),
  };
}

/** Every refund the engine records as paid, oldest year first. */
function _refundsPaid() {
  return Object.keys(_years).map(Number).sort((a, b) => a - b).reduce((out, y) => {
    const p = _paidRefund(_years[y]);
    if (p) out.push(p);
    return out;
  }, []);
}

/**
 * Push the paid-refund facts onto the bridge. Called after every mutation of `_years` —
 * IDB load, manual import, cloud fetch — because a consumer that reads a stale published
 * set is the same silent-staleness bug in a new place. Nothing is published until the
 * snapshots have actually been read, so "no years loaded" stays distinguishable from
 * "no refund paid".
 */
let _published = '\u0000';

function _publish() {
  // `_loaded`, NOT "are there any years". An install with no tax snapshots has an EMPTY set,
  // not an unread one, and publishing null there pinned an UNKNOWN row in the watchdog that
  // no action could ever clear — while occupying one of the four slots in the action bell and
  // pushing a real warning out of it. The flag says whether anyone looked. (audit 2026-08-10)
  const next = _loaded ? _refundsPaid() : null;
  // The engine-run date per snapshot, on the same `_loaded` gate and for the same reason: an
  // unread store must not report a fresh engine, and an install with no snapshots must not
  // report a stale one. (2026-08-17)
  const seen = _loaded
    ? Object.keys(_years).map(y => ({ year: Number(y), generated: (_years[y] || {}).generated || null }))
        .sort((a, b) => a.year - b.year)
    : null;
  // ⚠ BOTH FACTS GO IN THE SIGNATURE. The first version of this built `sig` from the refunds
  // alone and published the dates underneath it — so a new engine run that changed nothing but
  // `generated` returned at the guard above and the freshness check never saw it. A staleness
  // signal behind a change-detector that cannot see staleness is worse than no signal: it goes
  // stale itself and reports the last thing it happened to notice.
  const sig = JSON.stringify([next, seen]);
  if (sig === _published) return;
  _published = sig;
  publishTaxRefunds(next);
  publishTaxSnapshots(seen);
  // The IDB read and the cloud fetch both land after the boot render, so MARKING is not
  // enough — a flag is only read on the next navigation, and he is sitting on the dashboard
  // watching an action bell that says the snapshots were never read. Paint them.
  // Terminates: the second pass sees an unchanged signature and returns above.
  markDirty('dash', 'integrity');
  try { renderOnly('dash', 'integrity'); } catch (_e) { /* pre-DOM boot: the flags stand */ }
}

/**
 * Open the one-time-income form pre-filled from a validated year.
 *
 * It stops at the form on purpose. The engine knows the assessment and the payment advice;
 * it does not know whether the money reached an account this app tracks, or whether the
 * owner already booked it by hand. He presses Save, so the receipt goes through the same
 * door, the same reconciliation-anchor guard and the same audit row as every other one.
 */
export function taxBookRefund(y) {
  const p = _paidRefund(_years[y]);
  if (!p) return toast('No paid refund on this year', 'a-r');
  // REFUSE A SECOND BOOKING. The band that carries this button is rendered from state, and
  // state can be a click behind — a stale amber band plus a button with no guard credited the
  // refund twice, as a non-audit row the reconciler then defends. The guard belongs on the
  // action, not on the paint. (audit 2026-08-10)
  const already = taxRefundBooking(S.oneTimeIncome, p);
  if (already) {
    renderTax();
    return toast('Already booked as one-time income on ' + (already.date || '?'), 'a-r');
  }
  // Partly booked is NOT unbooked. Pre-filling the full amount on top of an existing
  // ₺9,366.31 entry credited the refund a second time and left the band shouting anyway —
  // the button became a loop that added the whole refund on every click. Naming the
  // discrepancy and refusing is the only move that cannot create money.
  const partial = taxRefundTagged(S.oneTimeIncome, p.year, p.currency);
  if (partial.sum > 0) {
    renderTax();
    return toast('Year ' + y + ' already carries ' + _fTk(partial.sum) + ' against a ' +
      _fTk(p.amount) + ' refund — correct that entry rather than adding another', 'a-r');
  }
  // Synchronous: showPage() is synchronous, so a timeout bought nothing and opened a window
  // in which he could navigate away from the modal being filled. #moOTI lives inside
  // #page-cashflow, so the navigation has to happen first or the modal opens inside a hidden
  // page and the click looks like it did nothing.
  if (window._navActivateTab) window._navActivateTab('cashflow');
  openOTIFor({
    type: 'Tax Refund',
    date: p.paidDate,
    amount: p.amount,
    currency: p.currency,
    note: 'Tax year ' + y + ' confirmed refund' +
      (p.confirmedBy ? ' — ' + p.confirmedBy : '') +
      (p.evidence ? ' · ' + p.evidence : ''),
    origin: { taxYear: y, taxRef: p.ref },
  });
  lg('tax', 'refund', 'Booking form opened for ' + y + ' — ' + _fTk(p.amount));
}

/**
 * One band per paid refund, for EVERY year on file rather than the selected one.
 *
 * The watchdog reports across all years, so its remedy — "Record in cash" — has to exist
 * across all years too. Rendered per selected year it sent him to a page where the button
 * was not, because the unbooked refund belonged to a year the chip row had not selected.
 */
function _refundBands() {
  const all = _refundsPaid();
  return all.map(p => {
    const booked = taxRefundBooking(S.oneTimeIncome, p);
    const head = all.length > 1 ? esc(String(p.year)) + ' · ' : '';
    if (booked) {
      return '<div class="tax-vband"><span class="badge">✓ IN CASH</span>' +
        '<span>' + head + 'Booked as one-time income ' + esc(String(booked.date || '')) + ' · ' + _fTk(booked.amount) +
        ' — inside every cash, surplus and free-TRY figure</span></div>';
    }
    // The SHORTFALL, not the refund. With ₺9,366.31 already booked against a ₺93,669.31
    // refund the band claimed the full amount was missing, which is a second wrong number
    // printed over the first one.
    const short = p.amount - taxRefundTagged(S.oneTimeIncome, p.year, p.currency).sum;
    return '<div class="tax-vband warn"><span class="badge">! NOT IN CASH</span>' +
      '<span>' + head + 'Paid ' + esc(p.paidDate) + ' and no cash record matches it. Until it is booked, ' +
      'cash, monthly surplus and free TRY are all ' + _fTk(short) + ' short.</span>' +
      '<button class="btn btn-s" style="margin-left:auto" data-action="taxBookRefund" ' +
      'data-args=\'[' + p.year + ']\'>Record in cash</button></div>';
  }).join('');
}

// ── main render ──
export function renderTax() {
  // Publish FIRST, and before the early return. Every path that mutates `_years` — IDB load,
  // manual import, cloud fetch — ends here, which makes this the one place the bridge can be
  // refreshed without a fourth caller forgetting. The DOM being absent is no reason for the
  // watchdog to keep reading a stale set.
  _publish();
  const body = document.getElementById('taxBody');
  if (!body) return;
  const r = _sel != null ? _years[_sel] : null;
  const yEl = document.getElementById('taxYears');
  if (yEl) {
    const ys = Object.keys(_years).map(Number).sort((a, b) => b - a);
    yEl.innerHTML = ys.map(y => {
      const rr = _years[y];
      const val = rr.official_validation && rr.declaration_replay &&
        Math.abs(rr.declaration_replay.refund - rr.official_validation.refund_confirmed) < 0.01;
      return '<button class="tax-ychip' + (y === _sel ? ' on' : '') + '" data-action="taxSelectYear" data-args=\'[' + y + ']\'>' +
        y + (val ? ' ✓' : '') + '</button>';
    }).join('');
  }
  const meta = document.getElementById('taxMeta');
  if (r) {
    const stale = (Date.now() - new Date(r.generated || 0).getTime()) > 35 * 864e5;
    if (meta) meta.innerHTML = (r.months_parsed < 12 ? 'banked <b>' + _fT(r.banked_refund) + '</b>' :
      'refund <b>' + _fTk(r.declaration_replay ? r.declaration_replay.refund : r.banked_refund) + '</b>') +
      ' · engine ' + esc(r.generated || '?') + ' · ' + (stale ? '<span style="color:var(--yel)">STALE</span>' : 'FRESH');
    body.innerHTML = _refundBands() + (r.months_parsed < 12 ? _liveView(r) : _fullYearView(r));
  } else {
    if (meta) meta.textContent = '';
    body.innerHTML = '<div class="panel tax-empty">' +
      '<div class="t1">No tax snapshot yet</div>' +
      '<div class="t2">Drop documents into the taxes folder — the engine and cloud sync do the rest. Or import a year\'s <b>results.json</b> manually:</div>' +
      '<div class="cmd">python3 engine/taxengine.py &lt;year&gt;</div>' +
      '</div>';
  }
}
